import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import { useApi } from "@core/api";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { EditorSection } from "@core/components/EditorFrame";
import { LabeledSelect } from "@core/components/forms/LabeledSelect";

const TAG_KEY = "DefaultShippingPackageId";

interface ExtendedTag {
  $op?: "upsert" | "update" | "create" | "delete";
  Guid?: string;
  Key: string;
  Value: string;
}

const ProductPackagePicker = () => {
  const api = useApi();
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const tags: ExtendedTag[] = useWatch({ control, name: "Tags", defaultValue: [] }) ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ["shippingPackages", "shippingOnly"],
    queryFn: () => api.ListPackage({ PageSize: 500 }),
  });

  const packages = useMemo(
    () => (data?.Results ?? []).filter(p => p.IsShippingPackage),
    [data],
  );

  const current = tags.find(x => x.Key === TAG_KEY && x.$op !== "delete");
  const currentValue = current?.Value ?? "";

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const existingIndex = tags.findIndex(x => x.Key === TAG_KEY);
    const next: ExtendedTag[] = [...tags];

    if (existingIndex >= 0) {
      const existing = next[existingIndex];
      if (!value) {
        next[existingIndex] = { ...existing, $op: "delete" };
      } else {
        next[existingIndex] = { ...existing, Value: value, $op: "upsert" };
      }
    } else if (value) {
      next.push({
        $op: "create",
        Guid: uuidv4(),
        Key: TAG_KEY,
        Value: value,
      });
    }

    setValue("Tags", next, { shouldDirty: true });
  };

  return (
    <EditorSection title={t("ui.shippingPackage", "Default Shipping Package")}>
      <LabeledSelect
        label={t("ui.defaultShippingPackage", "Default Shipping Package")}
        value={currentValue}
        onChange={handleChange}
        disabled={isLoading}
      >
        <option value="">{t("ui.useProductDimensions", "Use product dimensions")}</option>
        {packages.map(p => (
          <option key={p.Id} value={String(p.Id)}>
            {p.Name ?? `#${p.Id}`}
          </option>
        ))}
      </LabeledSelect>
    </EditorSection>
  );
};

registerExtension(
  "admin.inventory.products.extraEditorSections",
  ProductPackagePicker,
  "ProductPackagePicker",
);
